import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import Landing from "./pages/Landing";
import Courses from "./pages/Courses";
import CourseDetail from "./pages/CourseDetail";
import LessonPlayer from "./pages/LessonPlayer";
import Dashboard from "./pages/Dashboard";
import InstructorDashboard from "./pages/InstructorDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import CourseEditor from "./pages/CourseEditor";
import PaymentsAdmin from "./pages/PaymentsAdmin";
import InteractiveLab from "./pages/InteractiveLab";
import LabsCatalog from "./pages/LabsCatalog";
import LabDetail from "./pages/LabDetail";
import LabRunner from "./pages/LabRunner";
import LabsManager from "./pages/LabsManager";
import AuthPage from "./pages/AuthPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import Pricing from "./pages/Pricing";
import Profile from "./pages/Profile";
import Certificates from "./pages/Certificates";
import RecruitmentApply from "./pages/RecruitmentApply";
import RecruitmentTrial from "./pages/RecruitmentTrial";

// Empty by default → same-origin relative "/api" (proxied to the backend by nginx).
// Works on any domain and avoids Mixed Content / CORS. Override with REACT_APP_BACKEND_URL if needed.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

// Auth Context
import { createContext, useContext } from "react";

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = async () => {
    const storedToken = localStorage.getItem("token");
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
      });
      setUser(response.data);
      setToken(storedToken);
    } catch (error) {
      localStorage.removeItem("token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, ...userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { token: newToken, ...userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth", { state: { from: location.pathname } });
    }
    if (!loading && user && roles && !roles.includes(user.role)) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate, location, roles]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return user ? children : null;
};

// App Router
function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/courses" element={<Courses />} />
      <Route path="/courses/:courseId" element={<CourseDetail />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/auth" element={<AuthPage />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/lesson/:lessonId" element={
        <ProtectedRoute>
          <LessonPlayer />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/certificates" element={
        <ProtectedRoute>
          <Certificates />
        </ProtectedRoute>
      } />
      <Route path="/payment/success" element={
        <ProtectedRoute>
          <PaymentSuccess />
        </ProtectedRoute>
      } />
      
      {/* Instructor Routes */}
      <Route path="/instructor" element={
        <ProtectedRoute roles={["instructor", "admin"]}>
          <InstructorDashboard />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute roles={["admin"]}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/course/:courseId" element={
        <ProtectedRoute roles={["admin", "instructor"]}>
          <CourseEditor />
        </ProtectedRoute>
      } />
      <Route path="/admin/payments" element={
        <ProtectedRoute roles={["admin"]}>
          <PaymentsAdmin />
        </ProtectedRoute>
      } />
      <Route path="/admin/labs" element={
        <ProtectedRoute roles={["admin", "instructor"]}>
          <LabsManager />
        </ProtectedRoute>
      } />
      
      {/* Labs catalog (public) */}
      <Route path="/labs" element={<LabsCatalog />} />
      {/* Prepared-lab runner (student flow, no environment picker) */}
      <Route path="/labs/:labId/run" element={
        <ProtectedRoute>
          <LabRunner />
        </ProtectedRoute>
      } />
      {/* Free practice runner (environment picker) */}
      <Route path="/labs/run/:environment" element={
        <ProtectedRoute>
          <InteractiveLab />
        </ProtectedRoute>
      } />
      {/* Lab detail (public) */}
      <Route path="/labs/:labId" element={<LabDetail />} />

      {/* Recruitment (public) */}
      <Route path="/recruitment/apply/:campaignId" element={<RecruitmentApply />} />
      <Route path="/recruitment/trial/:candidateId" element={<RecruitmentTrial />} />
    </Routes>
  );
}

function App() {
  // Seed database on first load
  useEffect(() => {
    const seedDb = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (error) {
        // Already seeded or error - ignore
      }
    };
    seedDb();
  }, []);

  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AuthProvider>
          <Toaster 
            position="top-right" 
            richColors 
            theme="dark"
            toastOptions={{
              style: {
                background: '#0f172a',
                border: '1px solid #1e293b',
                color: '#f8fafc',
              },
            }}
          />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
